import { Component, OnInit } from '@angular/core';
import { AlertService } from '@app/_services';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ReportService } from '../../report.service';

interface TagSelectAPI {
	tagId: number;
	name: string;
	tagType: number;
	tagGroupCategory: string;
}

interface OrderProductSelectAPI {
	orderProductId: number;
	name: string;
}

@Component({
	selector: 'app-tag-revenue-report',
	templateUrl: './tag-revenue-report.component.html',
	styleUrls: ['./tag-revenue-report.component.css'],
	providers: [MessageService, ConfirmationService],
	styles: [`
		:host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
			position: -webkit-sticky;
			position: sticky;
			top: 0px;
		}

		@media screen and (max-width: 64em) {
			:host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
				top: 99px;
			}
		}
	`],
	standalone: false
})
export class TagRevenueReportComponent implements OnInit {

	columns = [
		{ field: 'tagsCsv',                 header: 'Tags' },
		{ field: 'saleDate',                header: 'Sale Date' },
		{ field: 'checkInDate',             header: 'Check-In' },
		{ field: 'checkOutDate',            header: 'Check-Out' },
		{ field: 'reservationId',           header: 'Res #' },
		{ field: 'orderId',                 header: 'Order #' },
		{ field: 'guestLastName',           header: 'Guest' },
		{ field: 'reservationStatus',       header: 'Res Type' },
		{ field: 'upsellRevenue',           header: 'Upsell Revenue' },
		{ field: 'totalReservationRevenue', header: 'Total Revenue' },
	];

	// Reservation status codes mirrored from FutureOccupancyReportComponent —
	// 2-char codes are what str_reservation.reservationStatus actually stores.
	// Labels are what admins know them by.
	readonly reservationStatusOptions = [
		{ label: 'Owner (OO)',       value: 'OO' },
		{ label: 'Owner Guest (OG)', value: 'OG' },
		{ label: 'Guest (OP)',       value: 'OP' },
		{ label: 'Maintenance (NA)', value: 'NA' },
		{ label: 'Canceled (OX)',    value: 'OX' },
		{ label: 'Closed (OC)',      value: 'OC' },
		{ label: 'Postponed (PP)',   value: 'PP' },
		{ label: 'Hold (QH)',        value: 'QH' },
		{ label: 'Open (QO)',        value: 'QO' },
		{ label: 'Expired (QE)',     value: 'QE' },
	];

	// Short labels shown in the result-table cell.
	readonly resStatusLabelMap: Record<string, string> = {
		OO: 'Owner',       OG: 'Owner Guest', OP: 'Guest',
		NA: 'Maintenance', OX: 'Canceled',    OC: 'Closed',
		PP: 'Postponed',   QH: 'Hold',        QO: 'Open',
		QE: 'Expired',
	};

	rows: any[] = [];
	loading = false;

	allTags: TagSelectAPI[] = [];
	allOrderProducts: OrderProductSelectAPI[] = [];

	selectedTagIds: number[] = [];
	selectedOrderProductIds: number[] = [];
	selectedReservationStatuses: string[] = [];

	startDate: Date;
	endDate: Date;
	checkOutDate: Date;
	saleDateStart: Date;
	saleDateEnd: Date;

	constructor(
		private alertService: AlertService,
		private messageService: MessageService,
		private reportService: ReportService,
	) { }

	ngOnInit(): void {
		const now = new Date();
		this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
		this.endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
		this.checkOutDate = null;
		this.saleDateStart = null;
		this.saleDateEnd = null;
		this.selectedReservationStatuses = [];

		this.reportService.tagRevenueSelectableTags().subscribe(
			res => { this.allTags = (res?.data as any)?.tags || []; },
			err => { this.alertService.error(err); }
		);

		this.reportService.tagRevenueSelectableOrderProducts().subscribe(
			res => { this.allOrderProducts = (res?.data as any)?.orderProducts || []; },
			err => { this.alertService.error(err); }
		);
	}

	onSearchSubmit(): void {
		const payload: any = {
			startDate:             this.toIsoDate(this.startDate),
			endDate:               this.toIsoDate(this.endDate),
			checkOutDate:          this.toIsoDate(this.checkOutDate),
			saleDateStart:         this.toIsoDate(this.saleDateStart),
			saleDateEnd:           this.toIsoDate(this.saleDateEnd),
			tagIdList:             this.selectedTagIds || [],
			orderProductIdList:    this.selectedOrderProductIds || [],
			reservationStatusList: this.selectedReservationStatuses || [],
		};

		this.loading = true;
		this.reportService.tagRevenueReport(payload).subscribe(
			res => {
				this.rows = (res?.data as any)?.tagRevenue || [];
				this.loading = false;
			},
			err => {
				this.alertService.error(err);
				this.loading = false;
			}
		);
	}

	resetFilters(): void {
		this.selectedTagIds = [];
		this.selectedOrderProductIds = [];
		this.selectedReservationStatuses = [];
		this.checkOutDate = null;
		this.saleDateStart = null;
		this.saleDateEnd = null;
		const now = new Date();
		this.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
		this.endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
	}

	/** Short display label for the reservation-status cell. */
	resolveResStatusLabel(code: string): string {
		return this.resStatusLabelMap[code] || code || '';
	}

	private toIsoDate(d: Date): string | null {
		if (!d) return null;
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${yyyy}-${mm}-${dd}`;
	}
}
